// A thin async wrapper over the vendored Stockfish 18 (lite, single-threaded)
// running in a Web Worker (plan §9). The engine is a UCI text pipe: we send
// line commands and receive line output. This class serialises searches (one
// `go` at a time), parses `info … score … pv …` and `bestmove`, and exposes a
// small promise API used by both the bot and the analysis board.
//
// The single-threaded build needs no SharedArrayBuffer, so it works whether or
// not the page is cross-origin isolated; the COOP/COEP headers (plan §11.3) just
// keep the door open for a threaded build later. The worker script derives its
// `.wasm` sibling from its own URL, so both live together in /engine/.

import type { Score } from "./eval.ts";

const ENGINE_URL = "/engine/stockfish.js";
const ENGINE_WASM_URL = "/engine/stockfish.wasm";

let warming: Promise<void> | null = null;

/**
 * Start filling the HTTP cache before a user enters a computer game. Course
 * completion is a natural idle window in which to fetch the 7 MB WASM asset.
 * Failures are intentionally silent; Engine still performs its normal load.
 */
export function warmEngineAssets(): Promise<void> {
  if (!warming) {
    warming = Promise.all(
      [ENGINE_URL, ENGINE_WASM_URL].map(async (url) => {
        const response = await fetch(url, { cache: "force-cache" });
        if (!response.ok) throw new Error(`engine preload ${response.status}`);
        await response.arrayBuffer();
      }),
    ).then(() => undefined);
    warming.catch(() => {
      warming = null;
    });
  }
  return warming;
}

export interface SearchInfo {
  depth: number;
  score: Score; // side-to-move perspective
  pv: string[]; // principal variation, UCI
  multipv?: number; // 1-based line rank (absent unless MultiPV > 1)
}

export interface SearchResult {
  bestmove: string | null; // null on "(none)" (no legal move / already ended)
  ponder?: string;
  info?: SearchInfo; // deepest info line seen for the top pv
  /** Deepest info per MultiPV rank (length 1 unless MultiPV was set). */
  infos?: SearchInfo[];
}

export interface GoOptions {
  fen: string;
  depth?: number;
  movetimeMs?: number;
  /** Search until `stop()` (continuous thinking). Ignores depth/movetime. */
  infinite?: boolean;
  /** Called on every usable info line with the deepest info per MultiPV rank so
   *  far — the live counterpart of `SearchResult.infos`, for a streaming eval
   *  bar and lines panel. */
  onInfos?: (infos: SearchInfo[]) => void;
}

type LineHandler = (line: string) => void;

export class Engine {
  private worker: Worker;
  private handlers = new Set<LineHandler>();
  private ready: Promise<void>;
  private chain: Promise<unknown> = Promise.resolve();
  private searching = false;
  private destroyed = false;
  /** Resolves the in-flight `go` when no `bestmove` will arrive (destroy). */
  private settle: (() => void) | null = null;

  constructor() {
    this.worker = new Worker(ENGINE_URL);
    this.worker.onmessage = (ev: MessageEvent) => {
      const line = typeof ev.data === "string" ? ev.data : String((ev.data as { data?: string })?.data ?? "");
      for (const h of [...this.handlers]) h(line);
    };
    this.ready = this.handshake();
  }

  /** Wait for `uci`/`isready` to settle; safe to await repeatedly. */
  init(): Promise<void> {
    return this.ready;
  }

  private async handshake(): Promise<void> {
    await this.send("uci", (l) => l === "uciok");
    await this.send("isready", (l) => l === "readyok");
  }

  /** `setoption name <name> value <value>`, flushed with an isready barrier. */
  async setOption(name: string, value: string | number): Promise<void> {
    await this.ready;
    this.post(`setoption name ${name} value ${value}`);
    await this.send("isready", (l) => l === "readyok");
  }

  /** Reset the engine's game state (transposition table, etc.). */
  async newGame(): Promise<void> {
    await this.ready;
    this.post("ucinewgame");
    await this.send("isready", (l) => l === "readyok");
  }

  /**
   * Search `opts.fen` and resolve once the engine replies `bestmove`. Calls are
   * serialised: a second `go` waits for the first to finish.
   */
  go(opts: GoOptions): Promise<SearchResult> {
    const run = this.chain.then(() => this.runGo(opts));
    // Keep the chain alive even if a search rejects (e.g. after destroy()).
    this.chain = run.catch(() => undefined);
    return run;
  }

  private async runGo(opts: GoOptions): Promise<SearchResult> {
    await this.ready;
    if (this.destroyed) return { bestmove: null };

    this.post(`position fen ${opts.fen}`);
    const byPv: (SearchInfo | undefined)[] = [];
    const dense = () => byPv.filter((i): i is SearchInfo => i !== undefined);

    return new Promise<SearchResult>((resolve) => {
      const handler: LineHandler = (line) => {
        if (line.startsWith("info ")) {
          const info = parseInfo(line);
          if (info) {
            byPv[(info.multipv ?? 1) - 1] = info;
            opts.onInfos?.(dense());
          }
          return;
        }
        if (line.startsWith("bestmove")) {
          this.handlers.delete(handler);
          this.searching = false;
          this.settle = null;
          const parts = line.split(/\s+/);
          const bm = parts[1] && parts[1] !== "(none)" ? parts[1] : null;
          const ponder = parts[2] === "ponder" ? parts[3] : undefined;
          const infos = dense();
          resolve({ bestmove: bm, ponder, info: infos[0], infos });
        }
      };
      this.handlers.add(handler);
      this.searching = true;
      // An `infinite` search only ends on `stop()`, and `destroy()` terminates the
      // worker without a `bestmove` — so hold the resolver to settle it there and
      // keep the `go` chain (and any awaiting caller) from hanging forever.
      this.settle = () => {
        this.handlers.delete(handler);
        this.searching = false;
        this.settle = null;
        const infos = dense();
        resolve({ bestmove: null, info: infos[0], infos });
      };

      if (opts.infinite) {
        this.post("go infinite");
        return;
      }
      const limits: string[] = [];
      if (opts.depth) limits.push(`depth ${opts.depth}`);
      if (opts.movetimeMs) limits.push(`movetime ${opts.movetimeMs}`);
      this.post(`go ${limits.join(" ") || "depth 12"}`);
    });
  }

  /** Ask the engine to stop the current search early (it still emits bestmove). */
  stop(): void {
    if (this.searching) this.post("stop");
  }

  destroy(): void {
    this.destroyed = true;
    const settle = this.settle;
    this.handlers.clear();
    try {
      this.post("quit");
    } catch {
      /* worker may already be gone */
    }
    this.worker.terminate();
    settle?.(); // the terminated worker will never answer the pending `go`
  }

  private post(cmd: string): void {
    this.worker.postMessage(cmd);
  }

  /** Send `cmd` and resolve when a line matches `done`. */
  private send(cmd: string, done: (line: string) => boolean): Promise<void> {
    return new Promise<void>((resolve) => {
      const handler: LineHandler = (line) => {
        if (done(line)) {
          this.handlers.delete(handler);
          resolve();
        }
      };
      this.handlers.add(handler);
      this.post(cmd);
    });
  }
}

/**
 * Parse a UCI `info` line into depth + score + pv. Returns `null` for lines
 * without a usable score/pv (e.g. bare `info currmove …`). Exported for tests.
 */
export function parseInfo(line: string): SearchInfo | null {
  const t = line.split(/\s+/);
  let depth = 0;
  let score: Score | undefined;
  let pv: string[] | undefined;
  let multipv: number | undefined;
  for (let i = 1; i < t.length; i++) {
    switch (t[i]) {
      case "depth":
        depth = Number(t[++i]) || 0;
        break;
      case "multipv":
        multipv = Number(t[++i]) || undefined;
        break;
      case "score": {
        const kind = t[++i];
        const val = Number(t[++i]);
        score = kind === "mate" ? { mate: val } : { cp: val };
        break;
      }
      case "pv":
        pv = t.slice(i + 1);
        i = t.length;
        break;
    }
  }
  if (!score || !pv || pv.length === 0) return null;
  return { depth, score, pv, ...(multipv !== undefined ? { multipv } : {}) };
}
